// ============================================================================
// LE LANCEUR DE LA BARRE DU HAUT — les autres applications web qu'un compte staff ouvre par la connexion unique.
//
// UTILITÉ : passer de la Console à l'EDI ou au store sans chercher d'adresse. Chaque ligne dit où l'application répond
// et comment elle répond en ce moment — la sonde de la page Santé, dite avec ses mots.
// ⚠️ AUCUNE ADRESSE ÉCRITE ICI : elle change d'un environnement à l'autre (bêta, production). Une application se
//    reconnaît à son IDENTIFIANT auprès de l'IdP, son adresse se lit dans ce qu'il déclare, rapprochée de sa sonde par
//    `platformApps` — la même carte que la page Applications, donc les deux écrans ne peuvent pas se contredire.
// ⚠️ CHAQUE APPLICATION S'OUVRE PAR SON ENTRÉE, pas par sa racine : la racine du store est son catalogue PUBLIC, sans
//    bouton de connexion depuis le 27/10 — un éditeur y entre par `/admin`, qui mène à l'IdP. Le lanceur visait la
//    racine, et l'éditeur atterrissait sur le catalogue sans aucun moyen de se connecter.
// ⚠️ PAS DE LIGNE « MODULE NON ACCORDÉ » : la Console ne sait pas qui a un compte dans l'application ouverte. Le store
//    n'ouvre plus qu'aux comptes qu'un éditeur a invités (passe de contrôle du 27/10) ; un compte non invité y lit le
//    refus, avec son motif. Le verrou viendra avec les droits par module (`CADRAGE-Droits-Modules-Licences.md`).
// ⚠️ LE BACK-OFFICE N'Y EST PAS : il porte les données d'un client, il s'ouvre depuis la fiche de chaque magasin.
// ============================================================================
import type { IconName } from '@orgazone/ui-web';
import type { RpApp, ServiceHealth } from './api';
import { platformApps } from './platform-apps';
import { HEALTH_STATUS } from './service-health';

/** Une application du lanceur : son identifiant auprès de l'IdP, son ENTRÉE (le chemin qui ouvre la connexion), et ce que la ligne en dit. */
export interface LauncherApp { clientId: string; name: string; desc: string; icon: IconName; entry: string }

/**
 * Les applications qu'un compte staff ouvre depuis la Console, dans l'ordre de la maquette.
 * ⚠️ L'identifiant est celui que l'IdP déclare par défaut (`apps/identity/server/src/clients.ts`) : un environnement
 *    qui le renomme verra sa ligne dire qu'elle n'est pas enregistrée, plutôt qu'ouvrir une autre application.
 */
export const LAUNCHER_APPS: readonly LauncherApp[] = [
  { clientId: 'edi', name: 'EDI', desc: 'Échanges de données avec les fournisseurs.', icon: 'swap', entry: '/' },
  // `/admin`, jamais la racine : la racine est le catalogue public, qui n'offre plus de connexion.
  { clientId: 'store', name: 'Store privé', desc: 'Installation et déploiement des applications sur les appareils.', icon: 'box', entry: '/admin' },
];

/** L'état d'une sonde en un mot, avec ses deux couleurs (pastille, texte). */
export interface LauncherHealth { label: string; dot: string; fg: string }

/** Une ligne du lanceur. `href` à `null` = l'application ne peut pas être ouverte, et `blocked` dit pourquoi. */
export interface LauncherRow extends LauncherApp {
  href: string | null;
  host: string | null;
  blocked: string | null;
  health: LauncherHealth;
}

/** Ce que le lanceur a lu : l'IdP et les sondes, chacun avec son échec. */
export type LauncherSource<T> = { ok: true; data: T } | { ok: false; error: string };

const NEUTRAL = { dot: HEALTH_STATUS.pending.dot, fg: HEALTH_STATUS.pending.fg };

/**
 * L'état d'une sonde dit comme la page Santé : la latence sur la MÉDIANE et la couleur du statut que le serveur a
 * jugé (paliers 100 / 500 ms). ⚠️ Le lanceur ne rejuge rien : un second jugement ferait dire « lent » ici à une
 * application que la page Santé déclare opérationnelle.
 */
export function probeHealth(s: ServiceHealth | null, svcError: string | null): LauncherHealth {
  if (svcError !== null) return { label: 'sondes illisibles', ...NEUTRAL };
  if (!s) return { label: 'aucune sonde', ...NEUTRAL };
  if (!s.enabled) return { label: 'sonde inactive', ...NEUTRAL };
  const st = HEALTH_STATUS[s.status];
  const ms = s.median ?? s.lastMs;
  if (s.status === 'pending' || s.status === 'down' || s.status === 'error' || ms === null) return { label: st.label, dot: st.dot, fg: st.fg };
  return { label: s.status === 'ok' ? `${ms} ms` : `${ms} ms · ${st.label}`, dot: st.dot, fg: st.fg };
}

/**
 * Les lignes du lanceur, ou `null` quand l'IdP n'a pas répondu : sans lui on ne connaît aucune adresse, et la connexion
 * unique ne fonctionnerait pas — l'écran le dit au lieu de proposer des lignes qui mèneraient à un échec.
 */
export function launcherRows(
  idp: LauncherSource<{ apps: RpApp[]; issuer: string | null }>,
  services: LauncherSource<ServiceHealth[]>,
): LauncherRow[] | null {
  if (!idp.ok) return null;
  const svcError = services.ok ? null : services.error;
  const cards = platformApps(services.ok ? services.data : [], idp.data.apps, idp.data.issuer);
  return LAUNCHER_APPS.map((app): LauncherRow => {
    const card = cards.find((c) => c.rp?.clientId === app.clientId) ?? null;
    const health = probeHealth(card?.service ?? null, svcError);
    if (!card) return { ...app, href: null, host: null, health, blocked: 'Pas enregistrée auprès de l’IdP de cet environnement : la connexion unique ne peut pas l’ouvrir.' };
    if (!card.origin) return { ...app, href: null, host: null, health, blocked: 'L’IdP déclare une adresse illisible pour cette application.' };
    return { ...app, href: new URL(app.entry, card.origin).href, host: new URL(card.origin).host, health, blocked: null };
  });
}
